import {
  type Ref, type RefObject, useCallback, useEffect, useImperativeHandle, useLayoutEffect, useRef, useState,
} from 'react'
import { invitation } from '../data/invitation.js'
import { useScrollScrub } from '../hooks/useScrollScrub.js'
import { measureNativeScrollDuration, scrollToContent } from '../lib/contentScroll.js'
import { ramp } from '../lib/math.js'
import { prefersReducedMotion } from '../lib/motion.js'
import { playbackProgress } from '../lib/playback.js'
import { shouldSeek, videoSeekTarget } from '../lib/scrub.js'
import { koreanTimeLine, shortDateLabel, weddingMoment } from '../lib/weddingDate.js'
import styles from './HeroFilm.module.css'

export interface HeroFilmHandle {
  play: () => void
}

export interface HeroFilmProps {
  src: string | undefined
  poster?: string
  ref?: Ref<HeroFilmHandle>
  videoRef?: RefObject<HTMLVideoElement | null>
  nextRef?: RefObject<HTMLElement | null>
  onScrollMode?: () => void
}

/** 봉투 클릭으로 시작하고 실제 재생 시간으로 문구를 전환하는 첫 영상. */
export function HeroFilm({ src, poster, ref, videoRef, nextRef, onScrollMode }: HeroFilmProps) {
  const moment = weddingMoment(invitation.weddingAt)
  const localVideo = useRef<HTMLVideoElement>(null)
  const video = videoRef ?? localVideo
  const wrap = useRef<HTMLDivElement>(null)
  const t1 = useRef<HTMLDivElement>(null)
  const t2 = useRef<HTMLDivElement>(null)
  const requested = useRef(false)
  const completed = useRef(false)
  const pending = useRef(false)
  const attempt = useRef(0)
  const frame = useRef<number | undefined>(undefined)
  const [blocked, setBlocked] = useState(false)
  const [scrollMode, setScrollMode] = useState(false)
  const transition = useRef<{ nextTop: number } | null>(null)
  const nativeDuration = useRef(450)
  const cancelMeasurement = useRef<(() => void) | undefined>(undefined)
  const cancelScroll = useRef<(() => void) | undefined>(undefined)

  const stopFrame = useCallback(() => {
    if (frame.current !== undefined) window.cancelAnimationFrame(frame.current)
    frame.current = undefined
  }, [])

  const paint = useCallback((progress?: number) => {
    const v = video.current
    const p = progress ?? (v === null ? 0 : playbackProgress(v.currentTime, v.duration))
    const reduced = prefersReducedMotion()
    const titleIn = ramp(p, 0, 0.14)
    const titleOut = ramp(p, 0.33, 0.48)
    const detailsIn = ramp(p, 0.5, 0.64)
    const detailsOut = ramp(p, 0.85, 0.99)
    if (t1.current !== null) {
      t1.current.style.opacity = String(titleIn * (1 - titleOut))
      t1.current.style.transform = reduced
        ? 'none'
        : 'translateY(' + ((1 - titleIn) * 12 - titleOut * 12).toFixed(1) + 'px)'
    }
    if (t2.current !== null) {
      t2.current.style.opacity = String(detailsIn * (1 - detailsOut))
      t2.current.style.transform = reduced
        ? 'none'
        : 'translateY(' + ((1 - detailsIn) * 12 - detailsOut * 12).toFixed(1) + 'px)'
    }
  }, [video])

  // click 핸들러에서 직접 호출해 사용자 제스처를 유지한다.
  // 첫 재생 중에만 호출하고, 완료 후에는 스크롤만 currentTime을 제어한다.
  const play = useCallback(() => {
    requested.current = true
    const v = video.current
    if (v === null || !v.getAttribute('src') || pending.current || completed.current) return
    const id = ++attempt.current
    pending.current = true
    setBlocked(false)
    // 실패한 리소스는 play만 재호출해도 error가 남는다. 사용자 재시도에서
    // 리소스를 다시 읽어 네트워크 복구 후 재생할 수 있게 한다.
    if (v.error) v.load()
    v.muted = true
    void v.play().then(() => {
      if (attempt.current === id) pending.current = false
    }).catch(() => {
      if (attempt.current !== id) return
      pending.current = false
      setBlocked(true)
    })
  }, [video])

  useImperativeHandle(ref, () => ({ play }), [play])

  useScrollScrub(wrap, (p) => {
    const v = video.current
    if (v !== null && v.readyState >= 1 && !(p >= 1 && v.ended)) {
      const target = videoSeekTarget(p, v.duration)
      if (shouldSeek(v.currentTime, target)) {
        try { v.currentTime = target } catch { /* 아직 시킹할 수 없는 리소스 */ }
      }
    }
    paint(p)
  }, scrollMode)

  // 트랙 확장은 useScrollScrub의 layout effect에서 먼저 적용된다.
  // 그만큼 scrollY를 보정해 현재 영상/본문 위치가 바뀌지 않게 한 뒤 이동한다.
  useLayoutEffect(() => {
    const before = transition.current
    const next = nextRef?.current
    if (!scrollMode || before === null) return
    transition.current = null
    if (next) {
      const delta = next.getBoundingClientRect().top - before.nextTop
      // Chrome이 scroll anchoring으로 먼저 이동했을 수 있으므로 현재 좌표에 보정한다.
      window.scrollTo({ top: window.scrollY + delta, behavior: 'instant' })
      if (before.nextTop > 1) cancelScroll.current = scrollToContent(next, nativeDuration.current)
    }
    onScrollMode?.()
  }, [scrollMode, nextRef, onScrollMode])

  useEffect(() => () => {
    cancelMeasurement.current?.()
    cancelScroll.current?.()
  }, [])

  useEffect(() => {
    setBlocked(false)
    paint()
    if (requested.current && src) play()
    const v = video.current
    return () => {
      attempt.current += 1
      pending.current = false
      stopFrame()
      v?.pause()
    }
  }, [src, paint, play, stopFrame, video])

  const handlePlaying = () => {
    if (!requested.current) return
    if (completed.current) {
      video.current?.pause()
      return
    }
    if (cancelMeasurement.current === undefined) {
      cancelMeasurement.current = measureNativeScrollDuration(
        nextRef?.current?.getBoundingClientRect().top ?? window.innerHeight,
        (duration) => { nativeDuration.current = duration },
      )
    }
    setBlocked(false)
    stopFrame()
    const tick = () => {
      paint()
      const v = video.current
      if (v !== null && !v.paused && !v.ended) {
        frame.current = window.requestAnimationFrame(tick)
      } else {
        frame.current = undefined
      }
    }
    tick()
  }

  const handleInterrupted = () => {
    stopFrame()
    if (requested.current && !completed.current && !video.current?.ended) {
      attempt.current += 1
      pending.current = false
      setBlocked(true)
    }
  }

  const handleEnded = () => {
    if (!requested.current || completed.current) return
    completed.current = true
    setBlocked(false)
    stopFrame()
    paint()
    const next = nextRef?.current
    transition.current = { nextTop: next?.getBoundingClientRect().top ?? 0 }
    setScrollMode(true)
  }

  return (
    <div ref={wrap} className={styles.wrap} data-playback={scrollMode ? 'scroll' : 'autoplay'} role="region" aria-label="첫 영상">
      <div className={styles.sticky}>
        <video
          ref={video}
          className={styles.video}
          muted
          playsInline
          preload="auto"
          onPlaying={handlePlaying}
          onWaiting={stopFrame}
          onTimeUpdate={() => { if (!completed.current) paint() }}
          onPause={handleInterrupted}
          onError={handleInterrupted}
          onEnded={handleEnded}
          {...(poster ? { poster } : {})}
          {...(src ? { src } : {})}
        />
        <div className={styles.toneWash} />
        <div className={styles.blur} />
        <div className={styles.gradient} />
        <div className={styles.textLayer}>
          <div ref={t1} className={styles.t1}>
            We&#39;re
            <br />
            Getting Married
          </div>
          <div ref={t2} className={styles.t2}>
            <div className={styles.dateBadge}>{shortDateLabel(moment)}</div>
            <div className={styles.placeLine}>
              {koreanTimeLine(moment)}
              <br />
              {invitation.venue.name}, {invitation.venue.hall}
            </div>
          </div>
        </div>
        {blocked && (
          <button type="button" className={styles.playButton} onClick={play}>
            영상 재생
          </button>
        )}
      </div>
    </div>
  )
}
